import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Modal, Select } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { attachToParent } from '../../services/subCollections';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import { attachCandidates } from '../../utils/familyActions';
import { normalizeId } from '../../utils/ids';
import { useCollections } from '../../contexts/CollectionsContext';

/**
 * ATTACH AN EXISTING COLLECTION… (docs/plans/sub-collections.md §5.8): the
 * owner puts one of their own top-level collections beneath this one. The
 * picker offers only what can go there (their own, ordinary, with no
 * sub-collections); the API decides the rest and its refusal is shown as it
 * words it — a card key the two families both use lists the keys.
 */
export default function AttachSubCollectionModal({ open, onClose, parent, onAttached }) {
	const { message } = App.useApp();
	// The shared collections list, asked again when the dialog opens.
	const { collections, available: collectionsLoaded, error: collectionsError, refresh: refreshCollections } = useCollections({ refreshOnOpen: open });
	const candidates = useMemo(() => (collectionsLoaded ? attachCandidates(parent, collections) : null), [collectionsLoaded, collections, parent]);
	const loadError = !collectionsLoaded && Boolean(collectionsError);
	const [chosen, setChosen] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!open) return undefined;
		setChosen(null);
		setRefusal(null);
	}, [open]);

	const picked = (candidates || []).find(c => normalizeId(c._id) === chosen) || null;

	const submit = async () => {
		if (!chosen) return;
		setBusy(true);
		setRefusal(null);
		try {
			await attachToParent(chosen, normalizeId(parent?._id));
			message.success(`${picked?.name || 'The collection'} is now a sub-collection of ${parent?.name || 'this collection'}.`);
			onAttached?.(chosen);
			onClose?.();
		} catch (error) {
			const keys = error?.response?.data?.keys;
			setRefusal(apiErrorCode(error) === 'card_key_collision' && Array.isArray(keys) && keys.length
				? `${apiErrorMessage(error)} The keys: ${keys.join(', ')}.`
				: apiErrorMessage(error, 'The collection could not be attached.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Attach an existing collection"
			okText="Attach"
			okButtonProps={{ disabled: !chosen, loading: busy }}
			onOk={submit}
			onCancel={onClose}
			destroyOnHidden
		>
			<RecordIdentity
				kicker="Becomes a sub-collection of"
				badge={<RecordBadge kind="collection" record={parent} title={parent?.name || 'Collection'} size="sm" />}
			/>
			<p>
				The collection keeps its own cards, items, settings and sharing. It can then use this
				collection&rsquo;s cards, and everyone with access here gets the same access there. If it has
				no time zone of its own, it counts its days in this collection&rsquo;s.
			</p>
			<DialogLoadState
				loaded={candidates !== null}
				error={loadError}
				label="your collections"
				onRetry={() => refreshCollections()}
				skeleton={<Skeleton variant='form-field' count={1} />}
				minHeight={64}
			>
				<Select
					style={{ width: '100%' }}
					placeholder={candidates && !candidates.length ? 'None of your collections can be attached' : 'Choose one of your collections'}
					disabled={!candidates?.length}
					value={chosen || undefined}
					onChange={setChosen}
					showSearch
					optionFilterProp="searchText"
					aria-label="Collection to attach"
					options={(candidates || []).map(c => ({
						value: normalizeId(c._id),
						searchText: c.name,
						label: <RecordBadge kind="collection" record={c} title={c.name || 'Collection'} size="xs" />,
					}))}
				/>
			</DialogLoadState>
			{refusal ? <Alert className="mt-2" type="warning" showIcon message={refusal} /> : null}
		</Modal>
	);
}
